// src/pages/IncidentForm.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card } from "react-bootstrap"; // ⬅️ add this import
import axios from "axios";

const IncidentForm = () => {
  // 🔐 Resolve user
  const navigate = useNavigate();
  const { id: paramId, incidentId } = useParams();
  const isNew = !incidentId;
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [incident, setIncident] = useState({
    inc_type: "",
    inc_date: "",
    inc_description: "",
    inc_truck_number: "",
    inc_trailers: [],
    inc_notes: [],
    inc_police_number: "",
    inc_call_in_time: "",
    inc_timezone: "",
    inc_incident_time: "",
    inc_location_country: "",
    inc_location_province: "",
    inc_preventable: null,
    inc_dot_reportable: null,
    reviewed: "Under Review",
    inc_vehicle_towed: "No",
    inc_driver_commercial: "No",
    inc_accident_highway: "No",
    inc_needs_insurance: "No",
    inc_cargo_damaged: "No",
    inc_under_dispatch: "No",
    inc_injuries: "No",
    inc_fatalities: "No",
    inc_ticket_issued: "No",
    inc_truck_damage: "",
    inc_trailer_damage: "",
    inc_container_damage: "",
    inc_chargeback_truck: "",
    inc_chargeback_trailer: "",
    inc_chargeback_container: "",
    inc_accident_type_id: "",
    inc_dangerous_goods_type: "",
    inc_other_details: "",
  });

  const [accidentTypes, setAccidentTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);

  useEffect(() => {
    const loadIncident = async () => {
      if (!isNew) {
        try {
          const { data } = await axios.get(
            `${API_BASE}/api/incidents/detail/${incidentId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          console.log("API RESPONSE:", data); // 👀 Add this
          // normalize date
          const normalizedDate = data.inc_date
            ? new Date(data.inc_date).toISOString().slice(0, 10)
            : "";

          // trailers + notes safe parse
          let trailers = [];
          let notes = [];
          try {
            trailers = Array.isArray(data.inc_trailers)
              ? data.inc_trailers
              : JSON.parse(data.inc_trailers || "[]");
          } catch {
            trailers = [];
          }
          try {
            notes = Array.isArray(data.inc_notes)
              ? data.inc_notes
              : JSON.parse(data.inc_notes || "[]");
          } catch {
            notes = [];
          }

          const {
            inc_trailers: rawTrailers,
            inc_notes: rawNotes,
            ...rest
          } = data;

          console.log("TRAILERS LOADED:", trailers);
          console.log("NOTES LOADED:", notes);

          setIncident((prev) => ({
            ...prev,
            ...rest,
            inc_userid: data?.inc_userid || userId,
            inc_date: normalizedDate,
            inc_trailers: trailers,
            inc_notes: notes,
            inc_preventable:
              data.inc_preventable === undefined ? null : data.inc_preventable,

            inc_dot_reportable:
              data.inc_dot_reportable === undefined
                ? null
                : data.inc_dot_reportable,

            reviewed: data?.reviewed || "Under Review",
            inc_accident_type_id: data?.inc_accident_type_id || "",
            inc_dangerous_goods_type: data?.inc_dangerous_goods_type || "",
            inc_other_details: data?.inc_other_details || "",
          }));
        } catch (err) {
          console.error("❌ Error loading incident", err);
        }
      }
    };

    loadIncident();
  }, [isNew, incidentId, API_BASE, userId]);

  useEffect(() => {
    if (incident.inc_location_country && countries.length > 0) {
      const countryId = countries.find(
        (c) => c.country_name === incident.inc_location_country
      )?.id;
      if (countryId) {
        setIncident((prev) => ({
          ...prev,
          inc_location_country: countryId,
        }));
      }
    }

    if (incident.inc_location_province && provinces.length > 0) {
      const provinceId = provinces.find(
        (p) => p.province_name === incident.inc_location_province
      )?.id;
      if (provinceId) {
        setIncident((prev) => ({
          ...prev,
          inc_location_province: provinceId,
        }));
      }
    }
  }, [
    countries,
    provinces,
    incident.inc_location_country,
    incident.inc_location_province,
  ]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/dropdowns/accident-types`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then(({ data }) => {
        setAccidentTypes(data);
      })
      .catch((err) => {
        console.error("❌ Error fetching accident types", err);
      });
  }, [API_BASE]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/dropdowns/countries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then(({ data }) => setCountries(data))
      .catch((err) => console.error("❌ Error fetching countries", err));
  }, [API_BASE]);

  useEffect(() => {
    if (incident.inc_location_country) {
      const selectedCountry = countries.find(
        (c) => String(c.id) === String(incident.inc_location_country)
      );

      if (selectedCountry && selectedCountry.country_name === "Canada") {
        axios
          .get(
            `${API_BASE}/api/dropdowns/provinces/${incident.inc_location_country}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          )
          .then(({ data }) => setProvinces(data))
          .catch((err) => console.error("❌ Error fetching provinces", err));
      } else {
        setProvinces([]);
      }
    }
  }, [incident.inc_location_country, countries, API_BASE]);

  // Load existing attachments when editing
  useEffect(() => {
    if (!isNew && incidentId) {
      axios
        .get(`${API_BASE}/api/incidents/attachments/${incidentId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => {
          const normalized = res.data.map((a) => ({
            id: a.ia_id,
            file: null,
            type: a.ia_type,
            ia_file: a.ia_file, // ✅ keep file path
            ia_original: a.ia_original, // ✅ keep original name
          }));
          setAttachments(normalized);
        })

        .catch((err) => console.error("❌ Error fetching attachments", err));
    }
  }, [isNew, incidentId, API_BASE]);

  const computeReviewSummary = (p, d) => {
    const pTxt =
      p === null
        ? "Under Review"
        : p === "Yes"
        ? "Preventable"
        : "Non-Preventable";

    const dTxt =
      d === null ? "" : d === "Yes" ? "DOT Reportable" : "Not DOT Reportable";

    return `${pTxt}${dTxt ? " - " + dTxt : " -"}`;
  };

  const getIncidentStatusDisplay = (incident) => {
    const summary = computeReviewSummary(
      incident.inc_preventable,
      incident.inc_dot_reportable
    );

    if (summary.startsWith("Under Review")) {
      return {
        text: summary,
        className: "alert alert-danger py-2 px-3 fw-semibold",
      };
    }

    if (incident.inc_preventable === "Yes") {
      return {
        text: `Reviewed > ${summary}`,
        className: "alert alert-warning py-2 px-3 fw-semibold",
      };
    }

    // Non-Preventable
    return {
      text: `Reviewed - ${summary}`,
      className: "alert alert-success py-2 px-3 fw-semibold",
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIncident((prev) => ({ ...prev, [name]: value }));
  };

  const updateReviewedStatus = (preventable) =>
    preventable === null ? "Under Review" : "Reviewed";

  const handleSubmit = async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    const payload = { ...incident, inc_userid: userId };

    try {
      let currentIncidentId = incidentId;

      if (incidentId && incidentId !== "new") {
        // 🔹 update existing
        await axios.put(`${API_BASE}/api/incidents/${incidentId}`, payload, {
          headers,
        });
        alert("✔️ Incident updated");
      } else {
        // 🔹 create new
        const res = await axios.post(`${API_BASE}/api/incidents`, payload, {
          headers,
        });

        currentIncidentId = res.data.id; // backend returns insertId
        alert("✔️ Incident created");
      }

      // 🔹 Upload pending attachments (if any)
      if (!currentIncidentId) {
        alert("Please save the incident first before uploading attachments.");
        return;
      }

      if (attachments.length > 0) {
        for (const a of attachments) {
          if (!a.file) continue;
          if (!a.type) {
            alert("Please select an attachment type");
            return;
          }

          const formData = new FormData();
          formData.append("file", a.file);
          formData.append("inc_id", currentIncidentId);
          formData.append("inc_userid", userId);
          formData.append("ia_type", a.type);

          await axios.post(
            `${API_BASE}/api/incidents/attachments/upload?userId=${userId}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        }
      }

      // Delete marked attachments
      for (const delId of deletedAttachmentIds) {
        await axios.delete(`${API_BASE}/api/incidents/attachments/${delId}`, {
          headers,
        });
      }

      // 🔹 Always redirect to edit mode of current incident
      navigate(`/incidents/${userId}/incident-detail/${currentIncidentId}`);
      window.location.reload();
    } catch (err) {
      console.error("Save error", err);
      alert("Save failed");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="text-center mb-4">
        <h3>Incident</h3>
      </div>

      <Card>
        <Card.Body>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                {incident.inc_number &&
                  (() => {
                    const status = getIncidentStatusDisplay(incident);

                    return (
                      <div>
                        <div className="fw-bold text-primary mb-1">
                          Incident #: {incident.inc_number}
                        </div>

                        <div className={status.className}>{status.text}</div>
                      </div>
                    );
                  })()}
              </div>

              <div>
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => navigate(`/incidents/${userId}`)}
                >
                  List All
                </button>
                <button type="submit" className="btn btn-success">
                  Save
                </button>
              </div>
            </div>

            {/* Incident Info */}
            <div className="card p-3 mb-3">
              <h5>Incident Information</h5>
              <div className="row">
                <div className="col-md-6 mb-2">
                  <label>Incident Type *</label>
                  <select
                    className="form-control"
                    name="inc_type"
                    value={incident.inc_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Accident">Accident</option>
                    <option value="Claim">Claim</option>
                    <option value="Hazmat Incident">Hazmat Incident</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* If Accident → show dropdown from DB */}
                {incident.inc_type === "Accident" && (
                  <div className="col-md-6 mb-2">
                    <label>Accident Type *</label>
                    <select
                      className="form-control"
                      name="inc_accident_type_id"
                      value={incident.inc_accident_type_id || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Accident Type</option>
                      {accidentTypes.length === 0 && (
                        <option disabled>⚠️ No types found</option>
                      )}
                      {accidentTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* If Hazmat Incident → show Dangerous Goods Type */}
                {incident.inc_type === "Hazmat Incident" && (
                  <div className="col-md-6 mb-2">
                    <label>Dangerous Goods Type *</label>
                    <select
                      className="form-control"
                      name="inc_dangerous_goods_type"
                      value={incident.inc_dangerous_goods_type || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                )}

                {/* If Other → show free text input */}
                {incident.inc_type === "Other" && (
                  <div className="col-md-6 mb-2">
                    <label>Other Details *</label>
                    <input
                      className="form-control"
                      name="inc_other_details"
                      value={incident.inc_other_details || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                )}
                <div className="col-md-6 mb-2">
                  <label>Police Number</label>
                  <input
                    className="form-control"
                    name="inc_police_number"
                    value={incident.inc_police_number}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <label>Truck Number</label>
                  <input
                    className="form-control"
                    name="inc_truck_number"
                    value={incident.inc_truck_number}
                    onChange={handleChange}
                  />
                </div>
                <div className="row">
                  <div className="col-md-3 mb-2">
                    <label>Call In Time</label>
                    <input
                      type="time"
                      name="inc_call_in_time"
                      value={incident.inc_call_in_time || ""}
                      onChange={handleChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-3 mb-2">
                    <label>Incident Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      name="inc_date"
                      value={incident.inc_date || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3 mb-2">
                    <label>Incident Time</label>
                    <input
                      type="time"
                      name="inc_incident_time"
                      value={incident.inc_incident_time || ""}
                      onChange={handleChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-3 mb-2">
                    <label>Timezone</label>
                    <select
                      name="inc_timezone"
                      value={incident.inc_timezone || ""}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Select Timezone</option>
                      <option value="PT (UTC-8 / UTC-7)">
                        Pacific Time (PT) — UTC -8:00 / -7:00
                      </option>
                      <option value="MT (UTC-7 / UTC-6)">
                        Mountain Time (MT) — UTC -7:00 / -6:00
                      </option>
                      <option value="CT (UTC-6 / UTC-5)">
                        Central Time (CT) — UTC -6:00 / -5:00
                      </option>
                      <option value="ET (UTC-5 / UTC-4)">
                        Eastern Time (ET) — UTC -5:00 / -4:00
                      </option>
                      <option value="AT (UTC-4 / UTC-3)">
                        Atlantic Time (AT) — UTC -4:00 / -3:00
                      </option>
                    </select>
                  </div>
                </div>

                <div className="col-12 mb-2">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    name="inc_description"
                    value={incident.inc_description}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Trailer */}
            <div className="card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Trailer</h5>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setIncident({
                      ...incident,
                      inc_trailers: [
                        ...incident.inc_trailers,
                        { number: "", type: "" },
                      ],
                    })
                  }
                >
                  ➕ Add Trailer
                </button>
              </div>

              {incident.inc_trailers.map((t, idx) => (
                <div key={idx} className="row mb-2 align-items-end">
                  <div className="col-md-5">
                    <label>Trailer {idx + 1} No.</label>
                    <input
                      className="form-control"
                      value={t.number}
                      onChange={(e) => {
                        const updated = [...incident.inc_trailers];
                        updated[idx].number = e.target.value;
                        setIncident({ ...incident, inc_trailers: updated });
                      }}
                    />
                  </div>
                  <div className="col-md-5">
                    <label>Trailer {idx + 1} Type</label>
                    <select
                      className="form-control"
                      value={t.type}
                      onChange={(e) => {
                        const updated = [...incident.inc_trailers];
                        updated[idx].type = e.target.value;
                        setIncident({ ...incident, inc_trailers: updated });
                      }}
                    >
                      <option value="">Select type</option>
                      <option value="Flatbed">Flatbed</option>
                      <option value="Dry Van">Dry Van</option>
                      <option value="Reefer">Reefer</option>
                      <option value="Tanker">Tanker</option>
                    </select>
                  </div>
                  <div className="col-md-2 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        const updated = incident.inc_trailers.filter(
                          (_, i) => i !== idx
                        );
                        setIncident({ ...incident, inc_trailers: updated });
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Location */}
            <div className="card p-3 mb-3">
              <h5>Location</h5>
              <div className="row">
                <div className="col-md-5">
                  <label>Country *</label>
                  <select
                    className="form-control"
                    name="inc_location_country"
                    value={incident.inc_location_country || ""}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.country_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-5">
                  <label>Province *</label>
                  {(() => {
                    const selectedCountry = countries.find(
                      (c) =>
                        String(c.id) === String(incident.inc_location_country)
                    );
                    if (
                      selectedCountry &&
                      selectedCountry.country_name === "Canada"
                    ) {
                      return (
                        <select
                          className="form-control"
                          name="inc_location_province"
                          value={incident.inc_location_province || ""}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Province</option>
                          {provinces.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.province_name}
                            </option>
                          ))}
                        </select>
                      );
                    }
                    return (
                      <input
                        className="form-control"
                        name="inc_location_province"
                        value={incident.inc_location_province || ""}
                        onChange={handleChange}
                        placeholder="Enter Province / State"
                      />
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Notes</h5>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setIncident({
                      ...incident,
                      inc_notes: [
                        ...incident.inc_notes,
                        { title: "", body: "", visible: true },
                      ],
                    })
                  }
                >
                  ➕ Add Note
                </button>
              </div>

              {incident.inc_notes.map((n, idx) => (
                <div key={idx} className="mb-3 border p-3 rounded bg-light">
                  <input
                    className="form-control mb-2"
                    placeholder="Title"
                    value={n.title}
                    onChange={(e) => {
                      const updated = [...incident.inc_notes];
                      updated[idx].title = e.target.value;
                      setIncident({ ...incident, inc_notes: updated });
                    }}
                  />
                  <textarea
                    className="form-control mb-2"
                    placeholder="Body"
                    value={n.body}
                    onChange={(e) => {
                      const updated = [...incident.inc_notes];
                      updated[idx].body = e.target.value;
                      setIncident({ ...incident, inc_notes: updated });
                    }}
                  />
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={n.visible}
                        onChange={(e) => {
                          const updated = [...incident.inc_notes];
                          updated[idx].visible = e.target.checked;
                          setIncident({ ...incident, inc_notes: updated });
                        }}
                      />
                      <label className="form-check-label">
                        Note is visible in Notes area of Personal Profile
                      </label>
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        const updated = incident.inc_notes.filter(
                          (_, i) => i !== idx
                        );
                        setIncident({ ...incident, inc_notes: updated });
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Questions */}
            {/* Incident / Accident Questions */}
            <div className="card p-3 mb-3">
              <h5>
                <i className="bi bi-exclamation-circle me-2"></i>
                Incident / Accident Questions
              </h5>
              <div className="row">
                <div className="col-md-6">
                  {[
                    ["inc_vehicle_towed", "Was the vehicle towed away?"],
                    [
                      "inc_driver_commercial",
                      "Was the driver in a commercial vehicle?",
                    ],
                    [
                      "inc_accident_highway",
                      "Did the accident happen on a public street or highway? (not in a parking lot, truck stop, shipper/consignee, etc.)",
                    ],
                    ["inc_needs_insurance", "Needs Insurance"],
                  ].map(([field, label]) => (
                    <div key={field} className="row mb-3 align-items-center">
                      <div className="col-8">
                        <span>{label}</span>
                      </div>
                      <div className="col-4 text-end">
                        <label className="me-2">
                          <input
                            type="radio"
                            name={field}
                            value="Yes"
                            checked={incident[field] === "Yes"}
                            onChange={handleChange}
                          />{" "}
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={field}
                            value="No"
                            checked={incident[field] === "No"}
                            onChange={handleChange}
                          />{" "}
                          No
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="col-md-6">
                  {[
                    ["inc_cargo_damaged", "Was Cargo Damaged?"],
                    ["inc_under_dispatch", "Under Dispatch?"],
                    ["inc_injuries", "Were there injuries?"],
                    ["inc_fatalities", "Were there fatalities?"],
                    ["inc_ticket_issued", "Was a ticket issued?"],
                  ].map(([field, label]) => (
                    <div key={field} className="row mb-3 align-items-center">
                      <div className="col-8">
                        <span>{label}</span>
                      </div>
                      <div className="col-4 text-end">
                        <label className="me-2">
                          <input
                            type="radio"
                            name={field}
                            value="Yes"
                            checked={incident[field] === "Yes"}
                            onChange={handleChange}
                          />{" "}
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={field}
                            value="No"
                            checked={incident[field] === "No"}
                            onChange={handleChange}
                          />{" "}
                          No
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Damage Amounts */}
            <div className="card p-3 mb-3">
              <div className="row">
                <div className="col-md-6">
                  <label>
                    <i className="bi bi-cash-coin me-1"></i> Truck Damage Amount
                  </label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    name="inc_truck_damage"
                    value={incident.inc_truck_damage}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label>
                    <i className="bi bi-cash-coin me-1"></i> Charge Back Amount
                  </label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    name="inc_chargeback_truck"
                    value={incident.inc_chargeback_truck}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label>
                    <i className="bi bi-cash-coin me-1"></i> Trailer Damage
                    Amount
                  </label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    name="inc_trailer_damage"
                    value={incident.inc_trailer_damage}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label>
                    <i className="bi bi-cash-coin me-1"></i> Charge Back Amount
                  </label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    name="inc_chargeback_trailer"
                    value={incident.inc_chargeback_trailer}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label>
                    <i className="bi bi-cash-coin me-1"></i> Container Damage
                    Amount
                  </label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    name="inc_container_damage"
                    value={incident.inc_container_damage}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label>
                    <i className="bi bi-cash-coin me-1"></i> Charge Back Amount
                  </label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    name="inc_chargeback_container"
                    value={incident.inc_chargeback_container}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <small className="text-muted">
                *Include only applicable amounts/charges
              </small>
            </div>

            {/* Determinations */}
            <div className="card p-3 mb-3">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <i className="bi bi-gavel me-2"></i>
                  Determinations
                </h5>
                {(() => {
                  const summary = computeReviewSummary(
                    incident.inc_preventable,
                    incident.inc_dot_reportable
                  );

                  const isUnderReview = summary.startsWith("Under Review");

                  if (!isUnderReview) {
                    // Everything normal → all black
                    return <span className="text-dark">{summary}</span>;
                  }

                  // Split "Under Review" from the rest
                  const rest = summary.replace("Under Review", "");

                  return (
                    <span>
                      <span className="text-danger">Under Review</span>
                      <span className="text-dark">{rest}</span>
                    </span>
                  );
                })()}
              </div>

              {/* Body */}
              <div className="row g-4">
                {/* Preventable */}
                <div className="col-md-6 border-end">
                  <div className="pe-md-3">
                    <label className="form-label">Preventable?</label>
                    <div className="btn-group w-100">
                      <button
                        type="button"
                        className={`btn ${
                          incident.inc_preventable === "Yes"
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() =>
                          setIncident((prev) => ({
                            ...prev,
                            inc_preventable: "Yes",
                            reviewed: updateReviewedStatus("Yes"),
                          }))
                        }
                      >
                        Preventable
                      </button>

                      <button
                        type="button"
                        className={`btn ${
                          incident.inc_preventable === null
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() =>
                          setIncident((prev) => ({
                            ...prev,
                            inc_preventable: null,
                            reviewed: updateReviewedStatus(null),
                          }))
                        }
                      >
                        <i className="bi bi-code-slash"></i>
                      </button>

                      <button
                        type="button"
                        className={`btn ${
                          incident.inc_preventable === "No"
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() =>
                          setIncident((prev) => ({
                            ...prev,
                            inc_preventable: "No",
                            reviewed: updateReviewedStatus("No"),
                          }))
                        }
                      >
                        Non-Preventable
                      </button>
                    </div>
                  </div>
                </div>

                {/* DOT Reportable */}
                <div className="col-md-6">
                  <div className="ps-md-3">
                    <label className="form-label">DOT Reportable?</label>
                    <div className="btn-group w-100">
                      <button
                        type="button"
                        className={`btn ${
                          incident.inc_dot_reportable === "Yes"
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() =>
                          setIncident((prev) => ({
                            ...prev,
                            inc_dot_reportable: "Yes",
                          }))
                        }
                      >
                        DOT Reportable
                      </button>

                      <button
                        type="button"
                        className={`btn ${
                          incident.inc_dot_reportable === null
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() =>
                          setIncident((prev) => ({
                            ...prev,
                            inc_dot_reportable: null,
                          }))
                        }
                      >
                        <i className="bi bi-code-slash"></i>
                      </button>

                      <button
                        type="button"
                        className={`btn ${
                          incident.inc_dot_reportable === "No"
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() =>
                          setIncident((prev) => ({
                            ...prev,
                            inc_dot_reportable: "No",
                          }))
                        }
                      >
                        Not DOT Reportable
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="card p-3 mb-3">
              <h5>Attachments</h5>

              {isNew ? (
                <div className="alert alert-info mb-0">
                  Please save the incident before uploading attachments.
                </div>
              ) : (
                <>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Attachment Type</label>
                      <select
                        className="form-control"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="Accident Report">Accident Report</option>
                        <option value="Witness Statement">
                          Witness Statement
                        </option>
                        <option value="Photos">Photos</option>
                        <option value="Ticket">Ticket</option>
                        <option value="Insurance Claim">Insurance Claim</option>
                        <option value="Corrective Action">
                          Corrective Action
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label>Choose File</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) => {
                          if (e.target.files[0] && selectedType) {
                            const fileObj = e.target.files[0];
                            setAttachments((prev) => [
                              ...prev,
                              {
                                id: null,
                                file: fileObj,
                                name: fileObj.name,
                                type: selectedType,
                              },
                            ]);
                            setSelectedType("");
                            e.target.value = ""; // ✅ reset input so you can pick another file
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Pending Attachments */}
                  {attachments.length > 0 && (
                    <ul className="list-group">
                      {attachments.map((a, idx) => (
                        <li
                          key={a.id || idx}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>
                            {a.id ? (
                              <a
                                href={`${API_BASE}/uploads/incidentattachments/${a.ia_file}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {a.ia_original}
                              </a>
                            ) : (
                              <a
                                href={URL.createObjectURL(a.file)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {a.file?.name || a.name || "Unnamed"}
                              </a>
                            )}{" "}
                            ({a.type || "Unknown"})
                          </span>

                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                              if (a.id) {
                                // mark this attachment for deletion later
                                setDeletedAttachmentIds((prev) => [
                                  ...prev,
                                  a.id,
                                ]);
                              }
                              // always remove from local list
                              setAttachments((prev) =>
                                prev.filter((_, i) => i !== idx)
                              );
                            }}
                          >
                            🗑
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default IncidentForm;
